import { Button, Input, Upload, UploadProps } from 'antd';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import cookies from 'react-cookies';
import { useDispatch, useSelector } from 'react-redux';
import { changePasswordSchema, SupplierSchema } from 'src/validates';

import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import userApi, { IUpdatPassword } from '@apis/user';
import { InputText, Selector } from '@components/shared';
import { userActions } from '@store/actions';
import { locationSelector } from '@store/location/location.slice';
import { userSelector } from '@store/user/user.slice';
import { IUser } from '@type/user';
import { beforeUpload, notify, removeVietnameseTones } from '@utils/index';
import { isEmpty } from 'lodash';

export function ProfileDetails() {
  const dispatch = useDispatch();
  const { user } = useSelector(userSelector);
  const { locations } = useSelector(locationSelector);
  const formik = useFormik<IUser>({
    enableReinitialize: true,
    initialValues: {
      name: '',
      email: '',
      avatar: '',
      phoneNumber: '',
      address: {
        street: null,
        ward: null,
        city: null,
      },
      ...user,
    },
    validationSchema: SupplierSchema,
    onSubmit: async (values) => {
      try {
        const response = await userApi.updateUserInfo(values);
        dispatch(userActions.self.getUserInfoSuccess(response.data));
        notify.success('Chỉnh sửa thông tin thành công');
      } catch (e) {
        notify.error(e?.message);
      }
    },
  });

  const formikChangePassword = useFormik<IUpdatPassword>({
    enableReinitialize: true,
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: changePasswordSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await userApi.updatePassword(values);
        notify.success('Chỉnh sửa nhà cung cấp thành công');
        resetForm();
      } catch (e) {
        notify.error(e?.message);
      }
    },
  });

  const [loading, setLoading] = useState(false);
  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      setLoading(false);
      if (info.file.response.data.path)
        formik.setFieldValue('avatar', info.file.response.data.path);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button">
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  return (
    <div className="py-4 lg:p-10">
      {/* Page Header */}
      <div className="px-4 md:px-0 mb-8">
        <h1 className="flex items-center gap-2 text-xl lg:text-2xl font-semibold text-gray-900 mb-2">
          <ChevronLeft
            className="cursor-pointer lg:hidden"
            onClick={() => {
              dispatch(userActions.self.setShowProfileSider(true));
            }}
            size={32}
          />
          THÔNG TIN CÁ NHÂN
        </h1>
        <p className="text-gray-600 text-sm">
          Quản lý thông tin cá nhân, bảo mật tài khoản, thay đổi mật khẩu và thiết lập quyền riêng
          tư.
        </p>
      </div>
      {/* Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Personal Information - Takes 2 columns on xl screens */}
        <div className="xl:col-span-2">
          <div className="bg-white rounded-xl shadow-sm md:border md:border-slate-100 p-4 md:p-6">
            <h2 className="text-xl font-medium text-gray-900 mb-6">Thông tin cá nhân</h2>
            {/* Avatar Section */}
            <div className="flex items-center mb-6">
              <Upload
                name="image"
                listType="picture-circle"
                className="avatar-uploader"
                showUploadList={false}
                action={`${process.env.NEXT_PUBLIC_ENDPOINT}/qdfood/api/v1/upload/image`}
                beforeUpload={beforeUpload}
                onChange={handleChange}
                headers={{
                  Authorization: `Bearer ${cookies.load('access_token') || ''}`,
                }}
                accept="image/*"
              >
                {formik?.values?.avatar ? (
                  <img
                    src={formik?.values?.avatar}
                    alt="avatar"
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
              <div className="ml-6">
                <h3 className="font-medium text-gray-900">Ảnh đại diện</h3>
                <p className="text-sm text-gray-600">JPG, PNG or GIF (tối đa 2MB)</p>
              </div>
            </div>

            {/* Form Fields */}
            <form onSubmit={formik.handleSubmit} className="space-y-3">
              {/* Name Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InputText
                  classNameInput="h-12 !text-base"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  required
                  name="name"
                  label="Tên"
                  placeholder="Nhập tên"
                  error={formik.errors?.name}
                />
                {/* Email */}
                <InputText
                  classNameInput="h-12 !text-base"
                  disabled
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  required
                  name="email"
                  label="Email"
                  placeholder="Nhập email"
                  error={formik.errors?.email}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InputText
                  className="col-span-2 md:col-span-1"
                  classNameInput="h-12"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  required
                  size="middle"
                  name="phoneNumber"
                  label="Số điện thoại"
                  placeholder="Nhập số điện thoại"
                  error={formik.errors?.phoneNumber}
                />
                <Selector
                  showSearch
                  allowClear
                  filterOption={(input, option: { label: string }) => {
                    return removeVietnameseTones(option?.label?.toLowerCase()).includes(
                      removeVietnameseTones(input.toLowerCase())
                    );
                  }}
                  className="col-span-2 md:col-span-1"
                  classNameSelector="h-12"
                  label="Thành phố / Tỉnh"
                  name="address.city"
                  placeholder="Chọn Thành phố/ tỉnh"
                  required
                  value={formik.values.address?.city}
                  onChange={(value) => {
                    formik.setFieldValue('address.city', value);
                    formik.setFieldValue('address.ward', null);
                  }}
                  options={locations?.cities
                    ?.sort((a, b) => parseInt(a?.code, 10) - parseInt(b.code, 10))
                    ?.map((city) => {
                      return {
                        value: city?.code,
                        label: city?.name_with_type,
                      };
                    })}
                  error={formik.errors?.address?.city}
                />
                <Selector
                  showSearch
                  allowClear
                  filterOption={(input, option: { label: string }) => {
                    return removeVietnameseTones(option?.label?.toLowerCase()).includes(
                      removeVietnameseTones(input.toLowerCase())
                    );
                  }}
                  className="col-span-2 md:col-span-1"
                  classNameSelector="h-12"
                  label="Phường / Xã"
                  name="address.ward"
                  placeholder="Chọn phường/ xã"
                  required
                  value={formik.values.address?.ward}
                  onChange={(value) => {
                    formik.setFieldValue('address.ward', value);
                  }}
                  options={locations?.wards
                    ?.sort((a, b) => parseInt(a?.code, 10) - parseInt(b?.code, 10))
                    ?.filter((ward) => ward?.parent_code == formik.values?.address?.city)
                    ?.map((ward) => {
                      return {
                        value: ward?.code,
                        label: ward?.name_with_type,
                      };
                    })}
                  error={formik.errors?.address?.ward}
                />
                <InputText
                  className="col-span-2 md:col-span-1"
                  classNameInput="h-12 !text-base"
                  value={formik.values?.address?.street}
                  onChange={formik.handleChange}
                  name="address.street"
                  required
                  label="Địa chỉ cụ thể"
                  placeholder="Nhập địa chỉ cụ thể"
                  error={formik.errors?.address?.street}
                />
              </div>
              {/* Save Button */}
              <Button
                disabled={!formik?.dirty || !isEmpty(formik?.errors)}
                loading={formik.isSubmitting}
                htmlType="submit"
                type="primary"
                className="w-full sm:w-[200px] h-12"
              >
                <span className="font-semibold typography-h5">Lưu thay đổi</span>
              </Button>
            </form>
          </div>
        </div>
        {/* Right Column */}
        <div className="space-y-6">
          {/* Password Change */}
          <div className="bg-white rounded-xl shadow-sm md:border md:border-slate-100 p-4 md:p-6">
            <h2 className="text-xl font-medium text-gray-900 mb-6">Đổi mật khẩu</h2>

            <form onSubmit={formikChangePassword.handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="current-password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mật khẩu hiện tại
                </label>
                <Input.Password
                  id="oldPassword"
                  name="oldPassword"
                  className="!h-12"
                  placeholder="Nhập mật khẩu"
                  value={formikChangePassword.values.oldPassword}
                  onChange={formikChangePassword.handleChange}
                  onBlur={formikChangePassword.handleBlur}
                  status={formikChangePassword.errors.oldPassword ? 'error' : ''}
                />
                <span className="text-red-500 text-sm">
                  {formikChangePassword.errors?.oldPassword}
                </span>
              </div>
              <div>
                <label
                  htmlFor="new-password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mật khẩu mới
                </label>
                <Input.Password
                  id="newPassword"
                  name="newPassword"
                  className="!h-12"
                  placeholder="Nhập mật khẩu"
                  value={formikChangePassword.values.newPassword}
                  onChange={formikChangePassword.handleChange}
                  onBlur={formikChangePassword.handleBlur}
                  status={formikChangePassword.errors.newPassword ? 'error' : ''}
                />
                <span className="text-red-500 text-sm">
                  {formikChangePassword.errors?.newPassword}
                </span>
              </div>
              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Xác nhận mật khẩu
                </label>
                <Input.Password
                  id="confirmPassword"
                  name="confirmPassword"
                  className="!h-12"
                  placeholder="Nhập mật khẩu"
                  value={formikChangePassword.values.confirmPassword}
                  onChange={formikChangePassword.handleChange}
                  onBlur={formikChangePassword.handleBlur}
                  status={formikChangePassword.errors.confirmPassword ? 'error' : ''}
                />
                <span className="text-red-500 text-sm">
                  {formikChangePassword.errors?.confirmPassword}
                </span>
              </div>
              <Button
                loading={formikChangePassword.isSubmitting}
                disabled={!formikChangePassword?.dirty || !isEmpty(formikChangePassword?.errors)}
                htmlType="submit"
                type="primary"
                className="h-12 w-full inline-flex items-center justify-center px-4 py-3 bg-orange-500 text-white rounded-md font-semibold"
              >
                Đổi mật khẩu
              </Button>
            </form>
          </div>
          {/* Account Status */}
          <div className="bg-white rounded-xl shadow-sm md:border md:border-slate-100 p-4 md:p-6">
            <h2 className="text-xl font-medium text-gray-900 mb-6">Trạng thái tài khoản</h2>
            <div>
              {/* Verified Status */}
              <div className="flex items-center justify-between p-3 bg-success-light rounded-lg">
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 text-success mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm text-gray-900">Trạng thái</span>
                </div>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                  Hoạt động
                </span>
              </div>
              {/* Join Date */}
              <div className="flex items-center justify-between p-3 bg-warning-light rounded-lg">
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 text-warning mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm text-gray-900">Ngày tham gia</span>
                </div>
                <span className="text-sm text-gray-600">
                  {dayjs(user?.createdAt).format('[Tháng] MM [năm] YYYY')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
